import React from "react";
import { Manifest } from "../../../../scripts/workflows/manifestHelper";
import { clearSchemaCache } from "./WorkflowBuilderV2/services/adapters/schemaCache";

let manifestHelper: any;

// initialize with empty manifest
loadManifest();

export function loadManifest(m?: any) {
    manifestHelper = new Manifest(m);
    clearSchemaCache();
}

export function loadManifestSchema(schema?: any) {
    if (!schema) {
        manifestHelper.loadManifestSchema(schema);
        clearSchemaCache();
        return;
    }

    const schemaToLoad = schema.schema ?? schema;
    manifestHelper.loadManifestSchema(schemaToLoad);
    clearSchemaCache();
}

export function getBlockTags(blockType: any, hidePopular = false) {
    const blockInfo = getBlockInfo(blockType);
    let tags = [];
    if (!blockInfo) return tags;

    if (blockInfo.popular && !hidePopular) {
        tags.push({
            id: "popular",
            content: (
                <>
                    <i className="fal fa-star" />
                    Popular
                </>
            )
        });
    }
    if (blockInfo.inDevelopment) {
        tags.push({
            id: "in-development",
            content: (
                <>
                    <i className="fal fa-hammer" />
                    In-Development
                </>
            )
        });
    }

    if (blockInfo.needsGPU || !!blockInfo.gpuHeavyDisclaimer) {
        tags.push({
            id: "needs-gpu",
            content: (
                <>
                    <i className="fal fa-microchip" />
                    Needs GPU
                </>
            ),
            tooltip: blockInfo.gpuHeavyDisclaimer
        });
    }

    if (blockInfo.section == "video") {
        tags.push({
            id: "video",
            content: (
                <>
                    <i className="fal fa-video" />
                    Video
                </>
            )
        });
    }

    if (blockInfo.enterprise_only) {
        tags.push({
            id: "enterprise-only",
            content: (
                <>
                    <i className="fal fa-building" />
                    Enterprise
                </>
            ),
            tooltip: "Only available for Enterprise plans"
        });
    }

    if (blockInfo.local_only) {
        tags.push({
            id: "local-only",
            content: (
                <>
                    <i className="fal fa-laptop" />
                    Local Only
                </>
            ),
            tooltip: "Can only be run in local mode"
        });
    }

    return tags;
}

export function getUQLDefs() {
    return manifestHelper.getUQLDefs();
}

export function getOperationsLookup() {
    return manifestHelper.getOperationsLookup();
}

export function getDataKinds() {
    return manifestHelper.getDataKinds();
}

export function calculateInputOutputTypes() {
    return manifestHelper.calculateInputOutputTypes();
}

export function getCompatibleWorkflowStepOutputRefs(
    workflowSpec: any,
    kinds: string[],
    currentStepId?: string,
    allowWildcard?: boolean
) {
    return manifestHelper.getCompatibleWorkflowStepOutputRefs(
        workflowSpec,
        kinds,
        currentStepId,
        allowWildcard
    );
}

export function getKindForWorkflowRef(workflowSpec: any, ref: string) {
    return manifestHelper.getKindForWorkflowRef(workflowSpec, ref);
}

export function listWorkflowStepIds(workflowSpec: any) {
    return manifestHelper.listWorkflowStepIds(workflowSpec);
}

export function getBlockInfo(blockIdentifier: string) {
    try {
        return manifestHelper.getBlockInfo(blockIdentifier);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to get block info for:", blockIdentifier, error);
        return null;
    }
}

export function getBlockIcon(blockIdentifier: string) {
    try {
        const icon = manifestHelper.getBlockIcon(blockIdentifier);
        return icon || { icon: "far fa-cube", color: "#6B7280" }; // Default fallback
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to get icon for block type:", blockIdentifier, error);
        return { icon: "far fa-cube", color: "#6B7280" }; // Default fallback
    }
}

export function getBlockSectionName(blockIdentifier: string) {
    try {
        const sectionName = manifestHelper.getBlockSectionName(blockIdentifier);
        return sectionName || "Unknown"; // Default fallback section name
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to get section name for block type:", blockIdentifier, error);
        return "Unknown"; // Default fallback section name
    }
}

export function getBlockColorScheme(blockIdentifier: string) {
    try {
        const colorScheme = manifestHelper.getBlockColorScheme(blockIdentifier);
        return colorScheme || "gray"; // Default fallback color scheme
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to get color scheme for block type:", blockIdentifier, error);
        return "gray"; // Default fallback color scheme
    }
}

export function getBlockDescription(blockIdentifier: string, type: "short" | "long" = "short") {
    return manifestHelper.getBlockDescription(blockIdentifier, type);
}

export function getBlockWarnings(blockIdentifier: string) {
    return manifestHelper.getBlockWarnings(blockIdentifier);
}

export function getBlockUsesSupervision(blockIdentifier: string) {
    return manifestHelper.getBlockUsesSupervision(blockIdentifier);
}

export function getBlockUsesInference(blockIdentifier: string) {
    return manifestHelper.getBlockUsesInference(blockIdentifier);
}

export function getBlockUsesOpenCV(blockIdentifier: string) {
    return manifestHelper.getBlockUsesOpenCV(blockIdentifier);
}

export function getBlockUsesTrackers(blockIdentifier: string) {
    return manifestHelper.getBlockUsesTrackers(blockIdentifier);
}

export function getBlockRequiresRoboflowKey(blockIdentifier: string) {
    return manifestHelper.getBlockRequiresRoboflowKey(blockIdentifier);
}

export function getBlockIsEnterpriseOnly(blockIdentifier: string) {
    return manifestHelper.getBlockIsEnterpriseOnly(blockIdentifier);
}

export function getBlockIsLocalOnly(blockIdentifier: string) {
    return manifestHelper.getBlockIsLocalOnly(blockIdentifier);
}

export function getBlockSourceCodeURL(blockIdentifier: string) {
    return manifestHelper.getBlockSourceCodeURL(blockIdentifier);
}

export function getCanonicalBlockType(blockIdentifier: string) {
    return manifestHelper.getCanonicalBlockType(blockIdentifier);
}

export function getVersionAgnosticBlockType(blockIdentifier: string) {
    return manifestHelper.getVersionAgnosticBlockType(blockIdentifier);
}

export function getBlockTypeForPolicyCheck(blockIdentifier: string) {
    return manifestHelper.getBlockTypeForPolicyCheck(blockIdentifier);
}

export function getCompatibleWorkflowInputRefs(workflowSpec: any, kinds: string[]) {
    return manifestHelper.getCompatibleWorkflowInputRefs(workflowSpec, kinds);
}

export function isConditionalFlowRef(blockType: string) {
    return manifestHelper.isConditionalFlowRef(blockType);
}

export function isDetectionTransformer(blockType: string) {
    return manifestHelper.isDetectionTransformer(blockType);
}

export function getInputRefKindsForInputProperty(blockType: string, input: string) {
    return manifestHelper.getInputRefKindsForInputProperty(blockType, input);
}

export function getWorkflowRefsAndSteps(workflow: any) {
    return manifestHelper.getWorkflowRefsAndSteps(workflow);
}

export function getInputDetectionProps(blockType: string) {
    return manifestHelper.getInputDetectionProps(blockType);
}

export function getBlockDisplayName(blockType: string) {
    try {
        const displayName = manifestHelper.getBlockDisplayName(blockType);
        return displayName || blockType;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Failed to get display name for block type:", blockType, error);
        return blockType;
    }
}

function camelToSnake(name: string): string {
    name = name.replace(/[/\- ]+/g, "_");
    name = name.replace(/(.)([A-Z][a-z]+)/g, "$1_$2");
    name = name.replace(/([a-z0-9])([A-Z])/g, "$1_$2");
    return name.toLowerCase().replace(/_+/g, "_").replace(/^_|_$/g, "");
}

export function getBlockDocsUrlSlug(blockType: string): string {
    const displayName = getBlockDisplayName(blockType);
    const isRawBlockType = displayName.includes("/") || displayName.includes("@");

    if (isRawBlockType) {
        let slug = displayName.split("@")[0];
        const slashIndex = slug.lastIndexOf("/");
        if (slashIndex !== -1) {
            slug = slug.substring(slashIndex + 1);
        }
        return camelToSnake(slug);
    }

    return camelToSnake(displayName);
}

export function getBlockTypes() {
    return manifestHelper.getBlockTypes();
}

export function getNewStepConfig(blockType: string, name: string) {
    return manifestHelper.getNewStepConfig(blockType, name);
}

export function hasMissingRefs(workflowSpec: any, data: any, blockType: string | null = null) {
    return manifestHelper.hasMissingRefs(workflowSpec, data, blockType);
}

export function getWorkflowRefs(workflowSpec: any) {
    return manifestHelper.getWorkflowRefs(workflowSpec);
}

export function isNewestBlockOfFamily(blockType: string) {
    return manifestHelper.isNewestBlockOfFamily(blockType);
}

export function isSegPreviewBlock(blockType: string) {
    return manifestHelper.isSegPreviewBlock(blockType);
}

export type RecommendedPlan = "webrtc-gpu-large" | "webrtc-gpu-medium" | "webrtc-gpu-small";
export type HeavyModelRecommendation = { modelName: string; recommendedPlan: RecommendedPlan };

// Models that require GPU Large for optimal performance
const HEAVY_MODELS: Record<string, RecommendedPlan> = {
    "roboflow_core/seg-preview": "webrtc-gpu-large",
    "roboflow_core/sam3": "webrtc-gpu-large"
};

export function getHeavyModelRecommendation(blocks: any[]): HeavyModelRecommendation | null {
    for (const block of blocks) {
        // Strip version suffix (e.g., "@v1") for lookup
        const blockType = block.type?.split("@")[0];
        const recommendedPlan = HEAVY_MODELS[blockType];
        if (recommendedPlan) {
            const blockInfo = getBlockInfo(block.type);
            const modelName = blockInfo?.human_friendly_block_name || blockType;
            return { modelName, recommendedPlan };
        }
    }
    return null;
}

export function getInputProps(blockType: string) {
    return manifestHelper.getInputProps(blockType);
}

export function getInputPropertiesOfKind(blockType: string, kind: string) {
    return manifestHelper.getInputPropertiesOfKind(blockType, kind);
}

export function isDetectionCombiner(blockType: string) {
    return manifestHelper.isDetectionCombiner(blockType);
}

export function getStepOutputRefKindsForInputProperty(blockType: string, input: string) {
    return manifestHelper.getStepOutputRefKindsForInputProperty(blockType, input);
}

export const detection_types_array = manifestHelper.detection_types_array;

export function getOutputPropertyKinds(blockType: string) {
    return manifestHelper.getOutputPropertyKinds(blockType);
}

export const detection_types = manifestHelper.detection_types;

export function getBlockSections() {
    return manifestHelper.getBlockSections();
}

export function getBlockUISpecification(blockType: string) {
    return manifestHelper.getBlockUISpecification(blockType);
}

export function isMissingRequiredProp(data: any) {
    return manifestHelper.isMissingRequiredProp(data);
}

export function isDetectionsPredictionKind(kind: string) {
    return manifestHelper.isDetectionsPredictionKind(kind);
}

export function isClassificationPredictionKind(kind: string) {
    return manifestHelper.isClassificationPredictionKind(kind);
}

export function isRoboflowModel(blockType: string) {
    return manifestHelper.isRoboflowModel(blockType);
}

export function isImageKind(kind: string) {
    return manifestHelper.isImageKind(kind);
}

export function isPopularBlock(blockType: string) {
    return manifestHelper.isPopularBlock(blockType);
}

export function isCustomBlock(blockType: string) {
    return manifestHelper.isCustomBlock(blockType);
}

export function getManifestSchema() {
    return manifestHelper.getManifestSchema();
}

export function getBlockKeywords(blockType: string) {
    return manifestHelper.getBlockKeywords(blockType);
}

export function onlyContainsWildcard(kinds: string[]) {
    return manifestHelper.onlyContainsWildcard(kinds);
}

export function kindsMatch(kinds1: string[], kinds2: string[]) {
    return manifestHelper.kindsMatch(kinds1, kinds2);
}

export function getLatestBlockTypeForFamily(blockFamily: string) {
    return manifestHelper.getLatestBlockTypeForFamily(blockFamily);
}

export function detectionConfidenceFilterDefinition(
    confidenceThreshold: number,
    confidenceOperator
) {
    return {
        type: "DetectionsFilter",
        filter_operation: {
            type: "StatementGroup",
            operator: "and",
            statements: [
                {
                    type: "BinaryStatement",

                    left_operand: {
                        type: "DynamicOperand",
                        operations: [
                            { type: "ExtractDetectionProperty", property_name: "confidence" }
                        ]
                    },
                    comparator: { type: `(Number) ${confidenceOperator}` },
                    right_operand: { type: "StaticOperand", value: confidenceThreshold }
                }
            ]
        }
    };
}

export function detectionClassFilterDefinition(filterMode: string, classList: string[]) {
    return {
        type: "DetectionsFilter",
        filter_operation: {
            type: "StatementGroup",
            operator: "and",
            statements: [
                {
                    type: "BinaryStatement",
                    negate: filterMode === "exclude",
                    left_operand: {
                        type: "DynamicOperand",
                        operations: [
                            { type: "ExtractDetectionProperty", property_name: "class_name" }
                        ]
                    },
                    comparator: { type: "in (Sequence)" },
                    right_operand: { type: "StaticOperand", value: classList }
                }
            ]
        }
    };
}

export function detectionClassAndConfidenceFilterDefinition(
    isClassFilteringActive: boolean,
    classSetInclusionMode: string,
    classList: Array<string>,
    isConfidenceFilteringActive: boolean,
    confidenceThreshold: number,
    confidenceOperator: string,
    detectionsParameterName: string = "_"
) {
    const statements = [];
    if (isClassFilteringActive === true && classList.length > 0) {
        statements.push({
            type: "BinaryStatement",
            negate: classSetInclusionMode !== "in",
            left_operand: {
                type: "DynamicOperand",
                operand_name: detectionsParameterName,
                operations: [{ type: "ExtractDetectionProperty", property_name: "class_name" }]
            },
            comparator: { type: "in (Sequence)" },
            right_operand: { type: "StaticOperand", value: classList }
        });
    }
    if (isConfidenceFilteringActive === true) {
        statements.push({
            type: "BinaryStatement",
            left_operand: {
                type: "DynamicOperand",
                operand_name: detectionsParameterName,
                operations: [{ type: "ExtractDetectionProperty", property_name: "confidence" }]
            },
            comparator: { type: `(Number) ${confidenceOperator}` },
            right_operand: { type: "StaticOperand", value: confidenceThreshold }
        });
    }
    return {
        type: "DetectionsFilter",
        filter_operation: { type: "StatementGroup", operator: "and", statements: statements }
    };
}

export function filterByDetectionsSizeDefinition(
    sizeThreshold: number,
    sizeThresholdOperator: string,
    imageOperandName: string,
    detectionsParameterName: string = "_"
) {
    return {
        type: "DetectionsFilter",
        filter_operation: {
            type: "StatementGroup",
            operator: "and",
            statements: [
                {
                    type: "BinaryStatement",
                    left_operand: {
                        type: "DynamicOperand",
                        operand_name: detectionsParameterName,
                        operations: [{ type: "ExtractDetectionProperty", property_name: "size" }]
                    },
                    comparator: { type: `(Number) ${sizeThresholdOperator}` },
                    right_operand: {
                        type: "DynamicOperand",
                        operand_name: imageOperandName,
                        operations: [
                            { type: "ExtractImageProperty", property_name: "size" },
                            { type: "Multiply", other: sizeThreshold / 100 }
                        ]
                    }
                }
            ]
        }
    };
}

export function filterByZoneDefinition(
    negateInZoneCheck: boolean,
    isZoneStatic: boolean,
    zonePoints: Array<Array<number>>,
    detectionReferencePoint: string,
    dynamicZoneParameterName: string,
    detectionsParameterName: string = "_"
) {
    let zoneOperand: any = { type: "StaticOperand", value: zonePoints };
    if (isZoneStatic !== true) {
        zoneOperand = { type: "DynamicOperand", operand_name: dynamicZoneParameterName };
    }
    return {
        type: "DetectionsFilter",
        filter_operation: {
            type: "StatementGroup",
            operator: "and",
            statements: [
                {
                    type: "BinaryStatement",
                    left_operand: {
                        type: "DynamicOperand",
                        operand_name: detectionsParameterName,
                        operations: [
                            {
                                type: "ExtractDetectionProperty",
                                property_name: detectionReferencePoint
                            }
                        ]
                    },
                    comparator: { type: "(Detection) in zone" },
                    right_operand: zoneOperand,
                    negate: negateInZoneCheck
                }
            ]
        }
    };
}

export function detectionsOffsetDefinition(detectionsOffset: Array<number>) {
    return {
        type: "DetectionsOffset",
        offset_x: detectionsOffset[0],
        offset_y: detectionsOffset[1]
    };
}

export function detectionsNumberBasedDefinition(
    detectionsOperations: Array<object>,
    detectionsParameterName: string,
    detectionsNumberOperator: string,
    detectionsNumberThreshold: number,
    choiceProbability: Partial<number>
) {
    const statements = [
        {
            type: "BinaryStatement",
            left_operand: {
                type: "DynamicOperand",
                operand_name: detectionsParameterName,
                operations: [...detectionsOperations, { type: "SequenceLength" }]
            },
            comparator: { type: `(Number) ${detectionsNumberOperator}` },
            right_operand: { type: "StaticOperand", value: detectionsNumberThreshold }
        }
    ];
    if (choiceProbability != null) {
        statements.push({
            type: "BinaryStatement",
            left_operand: {
                type: "DynamicOperand",
                operand_name: detectionsParameterName,
                operations: [{ type: "RandomNumber" }]
            },
            comparator: { type: `(Number) <=` },
            right_operand: { type: "StaticOperand", value: choiceProbability / 100 }
        });
    }
    return { type: "StatementGroup", statements: statements, operator: "and" };
}

export function randomBasedEvalDefinition(choiceProbability: number) {
    return {
        type: "StatementGroup",
        statements: [
            {
                type: "BinaryStatement",
                left_operand: { type: "DynamicOperand", operations: [{ type: "RandomNumber" }] },
                comparator: { type: `(Number) <=` },
                right_operand: { type: "StaticOperand", value: choiceProbability / 100 }
            }
        ]
    };
}

export function aggregatedDetectionsConfidenceEvalDefinition(
    detectionsParameterName: string,
    confidenceAggregationMode: string,
    confidenceAggregationOperator: string,
    confidenceAggregationThreshold: number,
    choiceProbability: Partial<number>
) {
    const statements: any = [
        {
            type: "BinaryStatement",
            left_operand: {
                type: "DynamicOperand",
                operand_name: detectionsParameterName,
                operations: [
                    { type: "DetectionsPropertyExtract", property_name: "confidence" },
                    {
                        type: "NumericSequenceAggregate",
                        function: confidenceAggregationMode,
                        neutral_value: 0.0
                    }
                ]
            },
            comparator: { type: `(Number) ${confidenceAggregationOperator}` },
            right_operand: { type: "StaticOperand", value: confidenceAggregationThreshold }
        }
    ];
    if (choiceProbability != null) {
        statements.push({
            type: "BinaryStatement",
            left_operand: {
                type: "DynamicOperand",
                operand_name: detectionsParameterName,
                operations: [{ type: "RandomNumber" }]
            },
            comparator: { type: `(Number) <=` },
            right_operand: { type: "StaticOperand", value: choiceProbability / 100 }
        });
    }
    return { type: "StatementGroup", statements: statements, operator: "and" };
}

export function imagePropertyEvalDefinition(
    extractedImageProperty: string,
    propertyValueOperator: string,
    referenceValue: any,
    isReferenceValueStatic: boolean,
    leftOperandName: string,
    referenceParameterName: string
) {
    let rightOperand = {};
    if (isReferenceValueStatic === true) {
        rightOperand = { type: "StaticOperand", value: referenceValue };
    } else {
        rightOperand = { type: "DynamicOperand", operand_name: referenceParameterName };
    }
    return {
        type: "StatementGroup",
        statements: [
            {
                type: "BinaryStatement",
                left_operand: {
                    type: "DynamicOperand",
                    operand_name: leftOperandName,
                    operations: [
                        { type: "ExtractImageProperty", property_name: extractedImageProperty }
                    ]
                },
                comparator: { type: `(Number) ${propertyValueOperator}` },
                right_operand: rightOperand
            }
        ]
    };
}

function convertStrToSequence(str: string) {
    return str.split(",").map((item) => item.trim());
}

export function classificationPropertyEvalDefinition(
    topClass: string,
    topClassConfidenceOperator: string,
    topClassConfidenceThreshold: number,
    isConfidenceFilteringActive: boolean,
    isMultiLabel: boolean,
    multiLabelOperation: "==" | "any in (Sequence)" | "all in (Sequence)"
) {
    let singleLabelClassStatement = {
        type: "BinaryStatement",
        left_operand: {
            type: "DynamicOperand",
            operand_name: "left",
            operations: [{ type: "ClassificationPropertyExtract", property_name: "top_class" }]
        },
        comparator: { type: "==" },
        right_operand: { type: "StaticOperand", value: topClass }
    };
    let multiLabelClassStatement = {
        type: "BinaryStatement",
        left_operand: { type: "StaticOperand", value: convertStrToSequence(topClass) },
        comparator: { type: multiLabelOperation },
        right_operand: {
            type: "DynamicOperand",
            operand_name: "left",
            operations: [{ type: "ClassificationPropertyExtract", property_name: "top_class" }]
        }
    };
    let classStatement = isMultiLabel ? multiLabelClassStatement : singleLabelClassStatement;
    let confidenceStatement = {
        type: "BinaryStatement",
        left_operand: {
            type: "DynamicOperand",
            operand_name: "left",
            operations: [
                {
                    type: "ClassificationPropertyExtract",
                    property_name: isMultiLabel
                        ? "top_class_confidence_single"
                        : "top_class_confidence"
                }
            ]
        },
        comparator: { type: `(Number) ${topClassConfidenceOperator}` },
        right_operand: { type: "StaticOperand", value: topClassConfidenceThreshold }
    };
    if (isConfidenceFilteringActive) {
        return {
            type: "StatementGroup",
            operator: "and",
            statements: [classStatement, confidenceStatement]
        };
    }
    return { type: "StatementGroup", statements: [classStatement] };
}

export function nonImageInputEvalDefinition(
    nonImageInputOperation: string,
    referenceValue: any,
    isReferenceValueStatic: boolean,
    leftOperandName: string,
    referenceParameterName: string
) {
    const unaryOperations = new Set([
        "Exists",
        "DoesNotExist",
        "(Boolean) is True",
        "(Boolean) is False",
        "(Sequence) is empty",
        "(Sequence) is not empty"
    ]);
    const isSelectedOperationUnary = unaryOperations.has(nonImageInputOperation);
    if (isSelectedOperationUnary) {
        return {
            type: "StatementGroup",
            statements: [
                {
                    type: "UnaryStatement",
                    operand: { type: "DynamicOperand", operand_name: leftOperandName },
                    operator: { type: nonImageInputOperation }
                }
            ]
        };
    }
    let rightOperand = {};
    if (isReferenceValueStatic === true) {
        rightOperand = { type: "StaticOperand", value: referenceValue };
    } else {
        rightOperand = { type: "DynamicOperand", operand_name: referenceParameterName };
    }
    return {
        type: "StatementGroup",
        statements: [
            {
                type: "BinaryStatement",
                left_operand: { type: "DynamicOperand", operand_name: leftOperandName },
                comparator: { type: nonImageInputOperation },
                right_operand: rightOperand
            }
        ]
    };
}
